/**
 * The legal pages' addresses (GH-812). One table, read by the router, the site footer, the
 * checkout's agreement line and the tests — and mirrored by `LegalPage` on iOS, which
 * `apps/ios-legal-links.test.ts` holds to these same paths.
 *
 * First-segment paths, like every other donor route: the static server answers any path it does
 * not own with the donor bundle (`bundleFor` in `infra/static-server.mjs`), so these need no
 * server rule.
 */
export const LEGAL_PATHS = {
  terms: "/terms",
  privacy: "/privacy",
  disclosures: "/disclosures",
  support: "/support",
  accountDeletion: "/account-deletion",
} as const;

export type LegalPageKey = keyof typeof LEGAL_PATHS;

/** The refund and dispute policy is a section of the Terms, reachable on its own. */
export const REFUNDS_HREF = `${LEGAL_PATHS.terms}#refunds`;

/** The footer's four, in the order the owner listed them. */
export const FOOTER_LINKS: ReadonlyArray<{ readonly key: LegalPageKey; readonly label: string }> = [
  { key: "terms", label: "Terms" },
  { key: "privacy", label: "Privacy" },
  { key: "disclosures", label: "Disclosures" },
  { key: "support", label: "Support" },
];

/** Which legal page a path names, tolerating one trailing slash. Null for every other path. */
export function legalPageFor(pathname: string): LegalPageKey | null {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  for (const [key, value] of Object.entries(LEGAL_PATHS) as Array<[LegalPageKey, string]>) {
    if (path === value) return key;
  }
  return null;
}
